import { useState, useEffect, useMemo } from "react";
import { BiPlus } from "react-icons/bi";
import { GrAscend, GrDescend } from "react-icons/gr";
import { PiLightningThin, PiMagnifyingGlass } from "react-icons/pi";
import { VscOrganization } from "react-icons/vsc";
import TableRowsLoader from "../Loaders/TableRowsLoader";
import { TbReload } from "react-icons/tb";
import { useNavigate, useParams } from "react-router-dom";
import ActionsTable from "./ActionsTable";
import NewActionModal from "./NewActionModal";
import DeleteItemModal from "../Components/DeleteItemModal";
import PopupMex from "../Components/SavePopup";
import { useLanguage } from "../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const MainActions = ({
    account_id,
    userData,
    orgData,
    fetchOrgData
}) => {
    const {t} = useLanguage()
    const navigate = useNavigate()
    const { org_id } = useParams()
    const [openModal, setOpenModal] = useState(false);
    const [openModalUpdate, setOpenModalUpdate] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");
    const [data, setDatata] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedMember, setSelectedMember] = useState({})
    const [openDeleteItem, setOpenDeleteItem] = useState(false)
    const [selectedItem, setSelectedItem] = useState({})
        const [popupMex, setPopupMex] = useState({ active: false, type: null, message: "" })

    const fetchData = async () => {
        if (!account_id) return;
        setLoading(true);
        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_actions`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    account_id,
                    org_id,
                    limit: 20,
                    offset: 0,
                }),
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.error || "Failed to fetch");

            if (result.error == "not_authorized") {
                setPopupMex({ active: true, type: "error", message: "Organization auth..." })
                setTimeout(() => {
                    setPopupMex({ active: false, type: null, message: "" })
                    navigate(`/e8console/organizations`)
                }, 5000);
            }

            setDatata(result.data || []);
        } catch (err) {
            console.error("Error fetching members:", err);
        } finally {
            setTimeout(() => setLoading(false), 500);
        }
    };

    useEffect(() => {
        fetchData();
    }, [account_id]);

    const filteredAndSortedData = useMemo(() => {
        if (!data) return [];

        let result = data.filter((member) => {
            const term = searchTerm.toLowerCase();
            const nameMatch = `${member.name} ${member.surname}`.toLowerCase().includes(term);
            const emailMatch = member.email?.toLowerCase().includes(term);
            const statusMatch = member.status?.toLowerCase().includes(term);

            const orgMatch = member.organizations?.some(org =>
                org.name?.toLowerCase().includes(term)
            );

            return nameMatch || emailMatch || orgMatch || statusMatch;
        });

        result.sort((a, b) => {
            const nameA = a.name?.toLowerCase() || "";
            const nameB = b.name?.toLowerCase() || "";
            return sortOrder === "asc"
                ? nameA.localeCompare(nameB)
                : nameB.localeCompare(nameA);
        });

        return result;
    }, [data, searchTerm, sortOrder]);

    const handleOpenModalUpdate = (item) => {
        setSelectedMember(item)
        setOpenModalUpdate(true)
    }

    if (loading) return <TableRowsLoader />;

    return (
        <div className="mainframe_item">
            <PopupMex active={popupMex.active} type={popupMex.type} message={popupMex.message} />
            <div className="mainframe_item_header">
                <p className="mainframe_item_title fade-in">{t.actions.title}</p>
                <div className="mainframe_item_subtitle fade-in">
                    {data.length === 0 ? (
                        <div className="empty_org_mex_cont">
                            <p>
                                {t.actions.empty_subtitle}
                            </p>
                            <div className="empty_org_img">
                                <p className="empty_org_img_title">
                                    <i><PiLightningThin /></i> {t.actions.quest_title} 
                                </p>
                                <p className="empty_org_img_subtitle">
                                   {t.actions.quest_answ}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <p>
                            {t.actions.subtitle} 
                        </p>
                    )}
                </div>
                <button
                    className={`mainframe_item_create_btn fade-in ${data.length === 0 && "mainframe_item_create_btn_anim"}`}
                    onClick={() => setOpenModal(true)}
                >
                    <BiPlus /> {t.create}
                </button>
            </div>

            {(data.length > 0 || searchTerm) && (
                <div className="filter_body_mainframe">
                    <div className="filtering_mainframe">
                        <div className="filtering_mainframe_btn_cont fade-in">
                            {data.length > 1 && (
                                <>
                                    <button className={`filter_btn ${sortOrder === "asc" ? "active" : ""}`} onClick={() => setSortOrder("asc")} title="A-Z">
                                        <GrAscend />
                                    </button>
                                    <button className={`filter_btn ${sortOrder === "desc" ? "active" : ""}`} onClick={() => setSortOrder("desc")} title="Z-A">
                                        <GrDescend />
                                    </button>
                                </>
                            )}
                            <button className="filter_btn" onClick={() => fetchData()} title={t.reload}>
                                <TbReload />
                            </button>
                        </div>
                        <div className="filter_input_cont fade-in">
                            <input
                                className="filter_input"
                                placeholder={t.search_filter}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <span className="filter_input_icon"><PiMagnifyingGlass /></span>
                        </div>
                    </div>

                    <ActionsTable
                        loading={loading}
                        data={data}
                        setSelectedItem={setSelectedItem}
                        setShowDeleteItem={setOpenDeleteItem}
                    />
                </div>
            )}

            <NewActionModal
                account_id={account_id}
                show={openModal}
                handleClose={() => setOpenModal(false)}
                fetchRecords={fetchData}
                userData={userData}
                orgData={orgData}
                fetchOrgData={fetchOrgData}
            />

            <DeleteItemModal
                account_id={account_id}
                show={openDeleteItem}
                handleClose={() => setOpenDeleteItem(false)}
                fetchRecords={fetchData}
                item={selectedItem}
                endpoint={"delete_action"}
                title={t.actions.delete_title}
                description={t.actions.delete_warning}
                input_value={selectedItem}
            />

        </div>
    );
};

export default MainActions;